import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { ArrowLeft, Mail, Clock, CheckCircle2, Pencil, RefreshCw, ExternalLink, Save } from "lucide-react";

/* /admin/book-cover-orders — Mike's queue for the $25 hand-designed covers.
   Lists all orders, lets him: change status, log a note, paste a draft URL,
   and bump the revision counter (max 3 included). */

const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const WINE  = "#0F172A";
const PAPER = "#FFFFFF";

const STATUS_OPTIONS = [
  { v: "new",        label: "New",        color: "#0F172A" },
  { v: "in-design",  label: "In Design",  color: "#287F8E" },
  { v: "delivered",  label: "Draft Sent", color: "#F39A1F" },
  { v: "revision",   label: "Revision",   color: "#F39A1F" },
  { v: "complete",   label: "Complete",   color: "#2E7D32" },
  { v: "cancelled",  label: "Cancelled",  color: "#7A7066" },
];

export default function AdminBookCoverOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});       // {orderId: {status, note, draft_url}}

  const load = async () => {
    try {
      const { data } = await api.get("/admin/book-cover/designed");
      setOrders(data?.orders || []);
    } catch (e) {
      toast.error("Couldn't load orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_admin) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;

  const setField = (id, key, val) =>
    setEditing((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: val } }));

  const save = async (id) => {
    const patch = editing[id] || {};
    try {
      await api.post(`/admin/book-cover/designed/${id}/update`, patch);
      toast.success("★ Updated");
      setEditing((e) => { const { [id]: _, ...rest } = e; return rest; });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed.");
    }
  };

  const bumpRevision = async (id) => {
    if (!confirm("Use one of the 3 included revisions?")) return;
    try {
      await api.post(`/admin/book-cover/designed/${id}/update`, { bump_revision: true });
      toast.success("Revision counted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't bump.");
    }
  };

  const statusBadge = (s) => {
    const opt = STATUS_OPTIONS.find((o) => o.v === s) || STATUS_OPTIONS[0];
    return (
      <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full text-white"
            style={{ background: opt.color }}>
        {opt.label}
      </span>
    );
  };

  const counts = STATUS_OPTIONS.reduce((acc, o) => {
    acc[o.v] = orders.filter((x) => (x.status || "new") === o.v).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ background: PAPER }} data-testid="admin-cover-orders-page">
      <SiteNav />

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <Link to="/admin/tutorials"
              data-testid="cover-orders-back-btn"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
              style={{ color: WINE }}>
          <ArrowLeft className="w-3.5 h-3.5" /> Admin
        </Link>

        <div className="mt-4 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GOLD }}>
              ★ Admin · Designed Cover Queue
            </div>
            <h1 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight mt-1"
                style={{ color: NAVY }}>
              Cover Orders Queue
            </h1>
          </div>
          <button onClick={load}
                  data-testid="orders-refresh-btn"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border hover:bg-white"
                  style={{ borderColor: NAVY, color: NAVY }}>
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Quick counts */}
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-2">
          {STATUS_OPTIONS.map((s) => (
            <div key={s.v}
                 data-testid={`count-${s.v}`}
                 className="rounded-md border-2 bg-white px-3 py-2 text-center"
                 style={{ borderColor: counts[s.v] > 0 ? s.color : "#E5E1D6" }}>
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: s.color }}>{s.label}</div>
              <div className="font-serif-display text-2xl" style={{ color: NAVY, fontWeight: 700 }}>{counts[s.v] || 0}</div>
            </div>
          ))}
        </div>

        {/* Orders */}
        <div className="mt-6 space-y-4">
          {loading && <div className="text-center text-foreground/60 py-12">Loading orders…</div>}
          {!loading && orders.length === 0 && (
            <div className="rounded-2xl border-2 bg-white p-10 text-center"
                 style={{ borderColor: "#E5E1D6" }}>
              <div className="font-bold text-lg" style={{ color: NAVY }}>No orders yet.</div>
              <div className="text-sm mt-1" style={{ color: "#7A7066" }}>
                Once a member orders a designed cover, it shows up here.
              </div>
            </div>
          )}
          {orders.map((o) => {
            const e = editing[o.id] || {};
            const status = e.status ?? (o.status || "new");
            const note   = e.note   ?? (o.admin_note || "");
            const draft  = e.draft_url ?? (o.draft_url || "");
            const dirty  = !!editing[o.id];
            return (
              <article key={o.id}
                       data-testid={`order-row-${o.id}`}
                       className="rounded-2xl border-2 bg-white p-5 shadow-sm"
                       style={{ borderColor: dirty ? GOLD : "#E5E1D6" }}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      {statusBadge(status)}
                      <span className="text-[11px] font-bold" style={{ color: WINE }}>
                        {o.revisions_used || 0} / {o.revisions_max || 3} revisions used
                      </span>
                    </div>
                    <h2 className="font-serif-display text-2xl uppercase leading-tight" style={{ color: NAVY }}>
                      {o.title}
                    </h2>
                    {o.subtitle && <div className="text-sm italic" style={{ color: "#3D3D3D" }}>{o.subtitle}</div>}
                    <div className="text-sm mt-1" style={{ color: NAVY }}>
                      <strong>by {o.author}</strong>
                    </div>
                  </div>
                  <div className="text-right text-xs" style={{ color: "#7A7066" }}>
                    <div>{new Date(o.created_at).toLocaleString()}</div>
                    <a href={`mailto:${o.contact || o.user_email}`}
                       className="inline-flex items-center gap-1 mt-1 font-bold hover:underline"
                       style={{ color: NAVY }}>
                      <Mail className="w-3.5 h-3.5" /> {o.contact || o.user_email}
                    </a>
                  </div>
                </div>

                {(o.vibe || o.must_have) && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                    {o.vibe && (
                      <div className="rounded-lg p-3" style={{ background: "#FFFFFF", border: `1px solid ${GOLD}55` }}>
                        <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>Vibe</div>
                        <div style={{ color: "#3D3D3D" }}>{o.vibe}</div>
                      </div>
                    )}
                    {o.must_have && (
                      <div className="rounded-lg p-3" style={{ background: "#FFFFFF", border: `1px solid ${GOLD}55` }}>
                        <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>Must Have</div>
                        <div style={{ color: "#3D3D3D" }}>{o.must_have}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Edit controls */}
                <div className="mt-4 grid sm:grid-cols-[180px_1fr] gap-3 items-start">
                  <label>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: NAVY }}>Status</div>
                    <select value={status}
                            onChange={(e) => setField(o.id, "status", e.target.value)}
                            data-testid={`order-status-${o.id}`}
                            className="w-full px-3 py-2 rounded-md border-2 text-sm font-bold"
                            style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}>
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.v} value={opt.v}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: NAVY }}>Draft URL (Dropbox, Drive, Imgur, etc.)</div>
                    <input type="text" value={draft}
                           onChange={(e) => setField(o.id, "draft_url", e.target.value)}
                           data-testid={`order-draft-${o.id}`}
                           placeholder="https://…"
                           className="w-full px-3 py-2 rounded-md border-2 text-sm"
                           style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }} />
                  </label>
                </div>

                <label className="block mt-3">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: NAVY }}>Note (visible to admin only)</div>
                  <textarea value={note}
                            onChange={(e) => setField(o.id, "note", e.target.value)}
                            rows={2}
                            data-testid={`order-note-${o.id}`}
                            className="w-full px-3 py-2 rounded-md border-2 text-sm"
                            style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }} />
                </label>

                <div className="mt-3 flex flex-wrap gap-2 justify-end items-center">
                  {o.draft_url && (
                    <a href={o.draft_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md border-2 hover:bg-white"
                       style={{ borderColor: NAVY, color: NAVY }}>
                      Open Draft <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <button type="button"
                          onClick={() => bumpRevision(o.id)}
                          disabled={(o.revisions_used || 0) >= (o.revisions_max || 3)}
                          data-testid={`bump-rev-${o.id}`}
                          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md border-2 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ borderColor: WINE, color: WINE }}>
                    <Pencil className="w-3 h-3" /> Use Revision ({(o.revisions_used || 0)}/{o.revisions_max || 3})
                  </button>
                  <button type="button"
                          onClick={() => save(o.id)}
                          disabled={!dirty}
                          data-testid={`save-order-${o.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider shadow disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
                    <Save className="w-3.5 h-3.5" /> Save
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
